/* eslint-disable */
import React, { useState } from "react";
import {
  Button,
  TextInput,
  FileInput,
  Stack,
  Select,
  Text,
  Loader,
  Group,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useAddSupportTicket } from "api/support/addsupport";
import { useUploadImages } from "api/support/useuploadimage";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";

export default function SupportForm({ onSuccess }: any) {
  const uploadImages = useUploadImages();
  const addTicket = useAddSupportTicket();
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm({
    initialValues: {
      title: "",
      description: "",
      images: [] as string[],
      priority: "high",
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: "",
    onUpdate: ({ editor }) => {
      form.setFieldValue("description", editor.getHTML());
    },
  });

  const handleSubmit = (values: any) => {
    if (!values.title || values.title.trim() === "") {
      showNotification({
        title: "Error",
        message: "Please enter a title.",
        color: "red",
      });
      return;
    }

    if (!values.description || values.description.trim() === "") {
      showNotification({
        title: "Error",
        message: "Please enter description.",
        color: "red",
      });
      return;
    }

    const imagesArray = Array.isArray(values.images) ? values.images : [];

    addTicket.mutate(
      {
        title: values.title,
        description: values.description,
        priority: values.priority,
        images: imagesArray,
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Success",
            message: "Support ticket created successfully!",
            color: "green",
          });

          form.reset();
          editor?.commands.clearContent();
          onSuccess?.();
        },
        onError: (err: any) => {
          showNotification({
            title: "Error",
            message: "Failed to create ticket.",
            color: "red",
          });
        },
      }
    );
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label="Ticket Title"
          placeholder="Enter your issue title"
          required
          {...form.getInputProps("title")}
        />

        <div>
          <Text size="sm" fw={500} mb={4}>
            Description
          </Text>
          {editor ? (
            <RichTextEditor editor={editor}>
              <RichTextEditor.Toolbar sticky stickyOffset={60}>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                  <RichTextEditor.Strikethrough />
                  <RichTextEditor.ClearFormatting />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.H1 />
                  <RichTextEditor.H2 />
                  <RichTextEditor.H3 />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.AlignLeft />
                  <RichTextEditor.AlignCenter />
                  <RichTextEditor.AlignRight />
                  <RichTextEditor.AlignJustify />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Link />
                  <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>

              <RichTextEditor.Content />
            </RichTextEditor>
          ) : (
            <Loader size="sm" />
          )}
        </div>

        <Select
          label="Priority"
          data={[
            { value: "low", label: "Low" },
            { value: "medium", label: "Medium" },
            { value: "high", label: "High" },
          ]}
          {...form.getInputProps("priority")}
        />

        <FileInput
          label={
            <Group spacing={8} align="center">
              <Text>Attach Images</Text>
              {isUploading && <Loader size="xs" />}
            </Group>
          }
          placeholder="Upload images"
          multiple
          accept="image/*"
          disabled={isUploading}
          onChange={async (files) => {
            if (!files || files.length === 0) return;

            setIsUploading(true);
            try {
              const res: any = await uploadImages.mutateAsync(files);

              const uploaded = res?.data ?? res;
              const ids =
                Array.isArray(uploaded) && uploaded.length > 0
                  ? uploaded.map((d: any) => d._id || d._id).filter(Boolean)
                  : [];

              const existing: string[] = Array.isArray(form.values.images)
                ? form.values.images
                : [];
              const newImages = [...existing, ...ids];

              form.setFieldValue("images", newImages);

              showNotification({
                title: "Uploaded",
                message: `Uploaded ${ids.length} image(s).`,
                color: "green",
              });
            } catch (err) {
              console.error("Image upload error:", err);
              showNotification({
                title: "Error",
                message: "Image upload failed",
                color: "red",
              });
            } finally {
              setIsUploading(false);
            }
          }}
        />

        <Button
          type="submit"
          loading={addTicket.isLoading}
          disabled={addTicket.isLoading}
        >
          Submit Ticket
        </Button>
      </Stack>
    </form>
  );
}
